import React, { useState, useEffect } from 'react';
import { Mail, Plus, X, CheckSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from './components/Sidebar';
import Header from './components/Header.jsx';
import MailPage from './pages/MailPage.jsx';
import AppRail from './components/AppRail';
import CalendarView from './components/CalendarView';
import LoginPage from './pages/LoginPage.jsx';
import RulesModal from './components/RulesModal.jsx';
import SettingsModal from './components/SettingsModal.jsx';
import ToDoView from './components/ToDoView/ToDo';
import { useMails } from './hooks/useMails.js';

const App = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(() => {
        return localStorage.getItem('xemail_sidebar_collapsed') === 'true';
    });
    const [user, setUser] = useState(() => {
        const savedUser = localStorage.getItem('xemail_user');
        return savedUser ? JSON.parse(savedUser) : null;
    });

    // Shared state from custom hook
    const mailState = useMails(user);
    const {
        activeFolder,
        setActiveFolder,
        isComposeOpen,
        setIsComposeOpen,
        unreadCount,
        inboxUnreadCount,
        searchQuery,
        setSearchQuery,
        storageUsage,
        availableIdentities
    } = mailState;
    const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isCalendarPanelOpen, setIsCalendarPanelOpen] = useState(() => {
        return localStorage.getItem('xemail_calendar_panel_open') === 'true';
    });
    const [activeApp, setActiveApp] = useState('mail');

    useEffect(() => {
        localStorage.setItem('xemail_calendar_panel_open', isCalendarPanelOpen.toString());
    }, [isCalendarPanelOpen]);

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        if (activeApp === 'calendar') {
            setActiveFolder('Calendar');
        } else if (activeApp === 'mail' && activeFolder === 'Calendar') {
            setActiveFolder('Inbox');
        }
    }, [activeApp]);

    useEffect(() => {
        if (activeFolder === 'Calendar') {
            setActiveApp('calendar');
        } else {
            setActiveApp('mail');
        }
    }, [activeFolder]);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isMobile = windowWidth <= 768;
    const isTablet = windowWidth <= 1024;

    const handleLogin = (email, password, token) => {
        const newUser = { email, password, name: email.split('@')[0], token };
        setUser(newUser);
        localStorage.setItem('xemail_user', JSON.stringify(newUser));
        localStorage.setItem('token', token); // Store raw token for easier access
    };

    const handleLogout = () => {
        setUser(null);
        localStorage.removeItem('xemail_user');
        localStorage.removeItem('token');
    };

    if (!user) {
        return <LoginPage onLogin={handleLogin} />;
    }

    return (
        <div className="app-container" style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'var(--bg-main)'
        }}>
            <Header
                isSidebarOpen={isSidebarOpen}
                setIsSidebarOpen={setIsSidebarOpen}
                setActiveFolder={setActiveFolder}
                setIsComposeOpen={setIsComposeOpen}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                user={user}
                onLogout={handleLogout}
                onOpenSettings={() => setIsSettingsOpen(true)}
                isCalendarPanelOpen={isCalendarPanelOpen}
                setIsCalendarPanelOpen={setIsCalendarPanelOpen}
            />

            <div style={{
                display: 'flex',
                flex: 1,
                overflow: 'hidden',
                position: 'relative',
                zIndex: (isComposeOpen && isMobile) ? 5000 : 1
            }}>
                <AppRail
                    activeApp={activeApp}
                    setActiveApp={setActiveApp}
                    isMobile={isMobile}
                />

                {activeApp === 'mail' && (
                    <Sidebar
                        activeFolder={activeFolder}
                        setActiveFolder={setActiveFolder}
                        isComposeOpen={isComposeOpen}
                        setIsComposeOpen={setIsComposeOpen}
                        isSidebarOpen={isSidebarOpen}
                        setIsSidebarOpen={setIsSidebarOpen}
                        isCollapsed={isCollapsed}
                        setIsCollapsed={setIsCollapsed}
                        unreadCount={unreadCount}
                        inboxUnreadCount={inboxUnreadCount}
                        storageUsage={storageUsage}
                        user={user}
                    />
                )}

                {/* Main and Calendar Container */}
                <div style={{
                    flex: 1,
                    display: 'flex',
                    overflow: 'hidden',
                    gap: isMobile ? '0' : '16px',
                    marginRight: isMobile ? '0' : '16px',
                    marginBottom: isMobile ? '0' : '16px'
                }}>
                    {/* Content Area */}
                    <main className="app-main-content" style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        borderRadius: isMobile ? '0' : '24px',
                        border: isMobile ? 'none' : '1px solid var(--border-glass)',
                        background: 'var(--bg-main)',
                        boxShadow: isMobile ? 'none' : '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
                        position: 'relative'
                    }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            {activeApp === 'todo' ? (
                                <ToDoView user={user} />
                            ) : activeFolder === 'Calendar' ? (
                                <CalendarView user={user} isSidePanel={false} />
                            ) : (
                                <MailPage {...mailState} user={user} onOpenRules={() => setIsRulesModalOpen(true)} />
                            )}
                        </div>
                    </main>

                    {/* Right Side Calendar Panel (Hidden when in Full Calendar view OR on Mobile/Tablet OR closed) */}
                    {activeFolder !== 'Calendar' && !isTablet && isCalendarPanelOpen && (
                        <motion.aside
                            initial={{ width: 0, opacity: 0 }}
                            animate={{ width: '320px', opacity: 1 }}
                            exit={{ width: 0, opacity: 0 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: '24px',
                                border: '1px solid var(--border-glass)',
                                background: 'var(--bg-main)',
                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)',
                                overflow: 'hidden'
                            }}
                        >
                            <CalendarView user={user} isSidePanel={true} />
                        </motion.aside>
                    )}
                </div>
            </div>

            <AnimatePresence>
                {isRulesModalOpen && (
                    <RulesModal
                        user={user}
                        onClose={() => setIsRulesModalOpen(false)}
                    />
                )}
                {isSettingsOpen && (
                    <SettingsModal
                        user={user}
                        onClose={() => setIsSettingsOpen(false)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default App;
